import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  EditOutlined,
  LinkOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { Button, Dropdown, Image, Popover, Skeleton, Table, Tag } from 'antd';
import Cookies from 'js-cookie';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiSolidPackage } from 'react-icons/bi';
import { Link, useNavigate } from 'react-router';
import { getBrandLogo } from '../../../../utils/fn/products';
import { useAssociatedProducts } from '../../../../utils/zustand';

const DragIndexContext = createContext({ active: -1, over: -1 });

const COLUMN_ORDER_COOKIE_NAME = 'productListColumnOrder';

const COOKIE_EXPIRES_DAYS = 30;

// TODO: use styled components for styling draggable columns
const dragActiveStyle = (dragState, id) => {
  const { active, over } = dragState;
  let style = {};
  if (active && active === id) {
    style = { backgroundColor: 'rgba(0, 0, 0, 0.05)', opacity: 0.5 };
  } else if (over && id === over && active !== over) {
    style = { borderInlineStart: '1px dashed #1890ff' };
  }
  return style;
};

const TableBodyCell = (props) => {
  const dragState = useContext(DragIndexContext);
  return (
    <td
      {...props}
      style={{ ...props.style, ...dragActiveStyle(dragState, props.id) }}
    />
  );
};

const TableHeaderCell = (props) => {
  const dragState = useContext(DragIndexContext);
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: props.id,
  });
  const style = {
    ...props.style,
    cursor: 'move',
    ...(isDragging
      ? { position: 'relative', zIndex: 9999, userSelect: 'none' }
      : {}),
    ...dragActiveStyle(dragState, props.id),
  };
  return (
    <th
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    />
  );
};

const ProductList = ({
  hasSelection = true,
  hasActions = true,
  stickyHeader = true,
  products,
  count,
  isLoading,
  currentPage = 1,
  pageSize = 100,
  listHeight = 500,
  onPageChange = () => {},
  onPageSizeChange = () => {},
  selectedProducts = [],
  setSelectedProducts = () => {},
}) => {
  const { t } = useTranslation(['products', 'common']);
  const navigate = useNavigate();
  const { updateAssociatedProducts, associatedProducts } =
    useAssociatedProducts();
  // État pour gérer le drag des colonnes
  const [dragIndex, setDragIndex] = useState({ active: -1, over: -1 });

  // Configuration de la sélection des lignes
  const rowSelection = {
    selectedRowKeys: selectedProducts,
    onChange: (selectedRowKeys, _selectedRows) => {
      setSelectedProducts(selectedRowKeys);

      // Récupérer les références des produits de la page courante
      const currentPageReferences = products.map((p) => p.reference);

      // Garder les produits associés qui ne sont PAS sur la page courante
      const productsFromOtherPages = associatedProducts.filter(
        (product) =>
          !currentPageReferences.includes(product.reference) &&
          selectedRowKeys.includes(product.reference)
      );

      // Ajouter les produits sélectionnés de la page courante
      const selectedProductsFromCurrentPage = products.filter((product) =>
        selectedRowKeys.includes(product.reference)
      );

      // Combiner les deux : produits des autres pages + sélection actuelle
      const updatedProducts = [
        ...productsFromOtherPages,
        ...selectedProductsFromCurrentPage,
      ];

      updateAssociatedProducts(updatedProducts);
    },
    preserveSelectedRowKeys: true,
    getCheckboxProps: (record) => ({
      name: record.reference,
    }),
  };

  const baseColumns = [
    {
      title: t('products:details.main_image'),
      width: 50,
      dataIndex: 'main_image',
      key: 'image',
      render: (main_image, record) => {
        return (
          <Image
            src={main_image}
            fallback="data:image/png;base64,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"
            alt={record.reference}
          />
        );
      },
      fixed: 'left',
    },
    {
      title: t('products:details.name'),
      // dataIndex: 'name',
      key: 'name',
      width: 200,
      // sorter: (a, b) => a.name?.localeCompare(b.name),
      render: (record) => {
        return (
          <Link
            to={`/dashboard/products/${encodeURIComponent(record.reference)}/structured-data`}
          >
            {record?.name || 'Product name placeholder'}
          </Link>
        );
      },
    },
    {
      title: t('products:details.reference'),
      onClick: (record) => {
        console.log('record', record);
      },
      // sorter: (a, b) => a.reference.localeCompare(b.reference),
      width: 120,
      dataIndex: 'reference',
      key: 'reference',
      render: (reference) => {
        return (
          <Link
            to={`/dashboard/products/${encodeURIComponent(reference)}/structured-data`}
          >
            {reference}
          </Link>
        );
      },
    },
    {
      title: t('products:details.category'),
      // sorter: (a, b) => a.category?.name.localeCompare(b.category?.name),
      dataIndex: ['category', 'name'],
      key: 'category',
      width: 200,
      render: (categoryName, _record) => <Tag color="blue">{categoryName}</Tag>,
    },
    {
      title: t('products:details.status'),
      width: 50,
      dataIndex: 'status',
      key: 'status',
      render: (record) => {
        return (
          <Tag color="green">{record?.status ? record.status : 'Completé'}</Tag>
        );
      },
    },
    {
      title: t('products:details.brand'),
      // sorter: (a, b) => a.brand.localeCompare(b.brand),
      dataIndex: 'brand',
      key: 'brand',
      width: 120,
      render: (brand) =>
        brand ? (
          <Image src={getBrandLogo(brand)} alt={brand.name} />
        ) : (
          <div></div>
        ),
    },
    {
      title: t('products:details.ean13'),
      dataIndex: 'ean13',
      key: 'ean13',
      width: 150,
      render: (ean13) => ean13 || '-',
    },
    {
      title: t('products:attributes.pack'),
      dataIndex: 'is_pack',
      key: 'is_pack',
      width: 100,
      render: (isPack) => (
        <Tag color={isPack ? 'green' : 'red'}>
          {isPack ? (
            <>
              <CheckCircleOutlined style={{ marginRight: 4 }} />
              {t('common:yes')}
            </>
          ) : (
            <>
              <CloseCircleOutlined style={{ marginRight: 4 }} />
              {t('common:no')}
            </>
          )}
        </Tag>
      ),
    },
    // FIXME: optimiser ceci pour éviter un calcul dans le render pour chaque ligne
    {
      title: t('products:attributes.composition'),
      dataIndex: 'composition',
      key: 'composition',
      width: 200,
      render: (composition) => {
        if (!composition) return '-';
        try {
          let comp;
          if (typeof composition === 'string') {
            // TODO: use a library to parse JSON instead of this hack
            const jsonString = composition.replace(/'/g, '"');
            comp = JSON.parse(jsonString);
          } else {
            comp = composition;
          }

          const entries = Object.entries(comp);
          if (entries.length === 0) return '-';

          const popoverContent = (
            <div style={{ maxWidth: 300 }}>
              {entries.map(([ref, qty]) => (
                <div key={ref} style={{ marginBottom: '4px' }}>
                  <Tag size="small" color="blue">
                    {ref}: {qty}
                  </Tag>
                </div>
              ))}
            </div>
          );

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Popover
                content={popoverContent}
                title="Composition complète"
                trigger="hover"
              >
                <Tag size="small" color="blue" style={{ cursor: 'pointer' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <BiSolidPackage size={16} />
                    <span>{entries.length} Colis</span>
                  </div>
                </Tag>
              </Popover>
            </div>
          );
        } catch (error) {
          console.error('Error parsing composition:', error);
          return composition;
        }
      },
    },
    {
      title: t('products:attributes.dimensions'),
      key: 'dimensions',
      width: 150,
      render: (_, record) => {
        const { width, length, height, depth } = record;
        const dimensions = [width, length, height, depth].filter(Boolean);
        return dimensions.length > 0 ? dimensions.join(' x ') : '-';
      },
    },
    {
      title: 'Produit principal',
      key: 'main_product',
      width: 150,
      render: (_, record) => {
        return record.main_product ? (
          <Tag color="blue">{record.main_product}</Tag>
        ) : (
          '-'
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 50,
      fixed: 'right',
      render: (_, record) => {
        const menuItems = [
          {
            key: 'update',
            label: t('common:update'),
            icon: <EditOutlined />,
            onClick: () => {
              console.log('Mettre à jour:', record.reference);
              // TODO: Implémenter la logique de mise à jour
            },
          },
          {
            key: 'associate',
            label: t('common:associate'),
            icon: <LinkOutlined />,
            onClick: () => {
              updateAssociatedProducts([record.reference]);
              navigate(`/dashboard/products/mp-association`);
            },
          },
          {
            key: 'export',
            label: t('common:export'),
            icon: <DownloadOutlined />,
            onClick: () => {
              console.log('Exporter:', record.reference);
              // TODO: Implémenter la logique d'export
            },
          },
        ];

        return (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button
                type="default"
                size="small"
                icon={<MoreOutlined />}
                onClick={(e) => e.stopPropagation()}
              />
            </Dropdown>
          </div>
        );
      },
    },
  ];

  const [columns, setColumns] = useState(() => {
    const filteredColumns = hasActions
      ? baseColumns
      : baseColumns.filter((col) => col.key !== 'actions');

    const columnsWithKeys = filteredColumns.map((column, i) => ({
      ...column,
      key: column.key || `col-${i}`,
      onHeaderCell: () => ({ id: column.key || `col-${i}` }),
      onCell: () => ({ id: column.key || `col-${i}` }),
    }));

    const savedOrder = Cookies.get(COLUMN_ORDER_COOKIE_NAME);
    if (savedOrder) {
      try {
        const orderArray = JSON.parse(savedOrder);
        const orderedColumns = orderArray
          .map((key) => columnsWithKeys.find((col) => col.key === key))
          .filter(Boolean);

        const newColumns = columnsWithKeys.filter(
          (col) => !orderArray.includes(col.key)
        );

        return [...orderedColumns, ...newColumns];
      } catch (error) {
        console.error('Erreur lors de la lecture des cookies:', error);
        return columnsWithKeys;
      }
    }

    return columnsWithKeys;
  });

  useEffect(() => {
    const columnOrder = columns.map((col) => col.key);
    Cookies.set(COLUMN_ORDER_COOKIE_NAME, JSON.stringify(columnOrder), {
      expires: COOKIE_EXPIRES_DAYS,
    });
  }, [columns]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setColumns((prevState) => {
        const activeIndex = prevState.findIndex((i) => i.key === active?.id);
        const overIndex = prevState.findIndex((i) => i.key === over?.id);
        return arrayMove(prevState, activeIndex, overIndex);
      });
    }
    setDragIndex({ active: -1, over: -1 });
  };

  const onDragOver = ({ active, over }) => {
    const activeIndex = columns.findIndex((i) => i.key === active.id);
    const overIndex = columns.findIndex((i) => i.key === over?.id);
    setDragIndex({
      active: active.id,
      over: over?.id,
      direction: overIndex > activeIndex ? 'right' : 'left',
    });
  };

  if (isLoading) {
    return (
      <Table
        size="small"
        columns={columns.map((column, index) => ({
          ...column,
          render: () =>
            index === 0 ? (
              <Skeleton.Image active style={{ width: 50, height: 50 }} />
            ) : (
              <Skeleton.Input
                active
                style={{ width: column.width || 120 }}
                size="small"
              />
            ),
        }))}
        dataSource={Array(10)
          .fill(null)
          .map((_, index) => ({ key: index }))}
        pagination={false}
        scroll={{ x: 'max-content', y: 550 }}
        sticky={stickyHeader ? { offsetHeader: 64 } : false}
      />
    );
  }

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      collisionDetection={closestCenter}
    >
      <SortableContext
        items={columns.map((i) => i.key)}
        strategy={horizontalListSortingStrategy}
      >
        <DragIndexContext.Provider value={dragIndex}>
          <Table
            columns={columns}
            dataSource={products}
            rowKey="reference"
            size="small"
            rowSelection={hasSelection ? rowSelection : undefined}
            pagination={{
              position: ['bottomRight'],
              current: currentPage,
              pageSize: pageSize,
              total: count,
              showSizeChanger: false,
              showQuickJumper: false,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${count} produits`,
              onChange: (page, size) => {
                onPageChange(page);
                if (size !== pageSize) {
                  onPageSizeChange(size);
                }
              },
              onShowSizeChange: (current, size) => {
                onPageSizeChange(size);
                onPageChange(1); // Reset to first page when changing page size
              },
            }}
            scroll={{ x: 'max-content', y: listHeight }}
            sticky={stickyHeader ? { offsetHeader: 64 } : false}
            components={{
              header: { cell: TableHeaderCell },
              body: { cell: TableBodyCell },
            }}
          />
        </DragIndexContext.Provider>
      </SortableContext>
      <DragOverlay>
        <th
          style={{
            backgroundColor: 'rgba(24, 144, 255, 0.1)',
            padding: 16,
            border: '1px solid #1890ff',
            borderRadius: 4,
          }}
        >
          {columns[columns.findIndex((i) => i.key === dragIndex.active)]?.title}
        </th>
      </DragOverlay>
    </DndContext>
  );
};

export default ProductList;
